import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { can } from "../permissions";
import { useCurrentUser } from "../hooks/useCurrentUser";
import {
  IconTrending, IconOrders, IconDevice, IconClock,
  IconSparkle, IconLoader, IconBranch, IconCash,
} from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";

// ---- Types (mirror backend DashboardOut) ----
interface SalesToday {
  total_minor: number;
  currency: string;
  order_count: number;
  open_count: number;
  open_total_minor: number;
  avg_order_minor: number;
}
interface DeviceSummary {
  id: string;
  name: string;
  is_active: boolean;
  license_expires_at: string | null;
  license_type: string | null;
  days_remaining: number | null;
}
interface DevicesSummary {
  total: number;
  active: number;
  offline: number;
  expiring_soon: number;
  expired: number;
  devices: DeviceSummary[];
}
interface OpenShift {
  id: string;
  employee_name: string;
  branch_name: string;
  started_at: string;
  duration_minutes: number;
}
interface RecentOrder {
  id: string;
  number: string | null;
  status: string;
  total_minor: number;
  currency: string;
  opened_at: string;
  closed_at: string | null;
  branch_name: string;
  table_label: string | null;
}
interface DashboardData {
  sales_today: SalesToday;
  devices: DevicesSummary;
  open_shifts: OpenShift[];
  recent_orders: RecentOrder[];
  branch_count: number;
  employee_count: number;
  selected_branch_id: string | null;
  selected_branch_name: string | null;
}

interface CashOverviewItem {
  branch_id: string;
  branch_name: string;
  shift_id: string;
  opened_at: string;
  employee_name: string;
  opening_cash_minor: number;
  cash_sales_minor: number;
  cash_ops_minor: number;
  current_cash_minor: number;
  currency: string;
}

interface BranchOption {
  id: string;
  name: string;
  code: string;
}

// ---- Helpers ----
function formatMoney(minor: number, currency: string): string {
  const value = minor / 100;
  return new Intl.NumberFormat("cs-CZ", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatTimeAgo(iso: string, t: (key: string, p?: Record<string, unknown>) => string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("dashboard.timeJustNow");
  if (mins < 60) return t("dashboard.timeMinutesAgo", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("dashboard.timeHoursAgo", { count: hours });
  const days = Math.floor(hours / 24);
  return t("dashboard.timeDaysAgo", { count: days });
}

function useStatusLabels(t: (key: string) => string): Record<string, string> {
  return {
    open: t("dashboard.statusOpen"),
    paying: t("dashboard.statusPaying"),
    paid: t("dashboard.statusPaid"),
    cancelled: t("dashboard.statusCancelled"),
    refunded: t("dashboard.statusRefunded"),
  };
}

const STATUS_BADGES: Record<string, string> = {
  open: "badge badge-yellow",
  paying: "badge badge-yellow",
  paid: "badge badge-green",
  cancelled: "badge badge-red",
  refunded: "badge badge-red",
};

export default function Dashboard() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [cashData, setCashData] = useState<CashOverviewItem[]>([]);
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [branchesLoaded, setBranchesLoaded] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const { user } = useCurrentUser();
  const canAccounts = can(user, "accounts");
  const canCashState = can(user, "cash_state");
  const canDevices = can(user, "devices");
  const canAttendance = can(user, "attendance");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch branches once
  useEffect(() => {
    api
      .get<BranchOption[]>("/branches?limit=500")
      .then((res) => {
        setBranches(res.data);
        if (res.data.length === 0) {
          // No branches — redirect to branch creation
          nav("/branches", { replace: true });
          return;
        }
      })
      .catch(() => {
        /* ignore — dashboard will still load */
      })
      .finally(() => setBranchesLoaded(true));
  }, [nav]);

  // Fetch dashboard data (re-fetch when branch selection changes)
  useEffect(() => {
    if (!branchesLoaded) return; // wait for branches to load first
    setLoading(true);
    const url = selectedBranch === "all"
      ? "/dashboard"
      : `/dashboard?branch_id=${selectedBranch}`;
    api
      .get<DashboardData>(url)
      .then((res) => setData(res.data))
      .catch(() => setError(t("dashboard.loadError")))
      .finally(() => setLoading(false));
  }, [selectedBranch, branchesLoaded]);

  // Fetch cash overview (per branch with open shift); refresh every 30s
  useEffect(() => {
    if (!branchesLoaded || !canCashState) return;
    const url = selectedBranch === "all"
      ? "/dashboard/cash"
      : `/dashboard/cash?branch_id=${selectedBranch}`;
    const fetchCash = () => {
      api
        .get<{ items: CashOverviewItem[] }>(url)
        .then((res) => setCashData(res.data.items))
        .catch(() => setCashData([]));
    };
    fetchCash();
    const id = setInterval(fetchCash, 30_000);
    return () => clearInterval(id);
  }, [selectedBranch, branchesLoaded, canCashState]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <IconLoader size={32} className="muted" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
        {error || t("dashboard.noData")}
      </div>
    );
  }

  const { sales_today: sales, devices: devs, open_shifts: shifts, recent_orders: orders } = data;
  const statusLabels = useStatusLabels(t);

  const allStats: { label: string; value: string; sub: string; icon: typeof IconTrending; gradient: string; to?: string }[] = [
    {
      label: t("dashboard.salesToday"),
      value: formatMoney(sales.total_minor, sales.currency),
      sub: sales.order_count > 0
        ? t("dashboard.salesTodaySub", { count: sales.order_count, avg: formatMoney(sales.avg_order_minor, sales.currency) })
        : t("dashboard.salesTodayEmpty"),
      icon: IconTrending,
      gradient: "var(--gradient-warm)",
      to: "/finance/receipts",
    },
    {
      label: t("dashboard.openOrders"),
      value: `${sales.open_count} / ${formatMoney(sales.open_total_minor, sales.currency)}`,
      sub: sales.open_count > 0
        ? t("dashboard.openOrdersAvg", { avg: formatMoney(Math.floor(sales.open_total_minor / sales.open_count), sales.currency) })
        : t("dashboard.openOrdersEmpty"),
      icon: IconOrders,
      gradient: "linear-gradient(135deg, #ff8c42, #ff6b6b)",
      to: "/finance/open-accounts",
    },
    {
      label: t("dashboard.cashOverview"),
      value: formatMoney(
        cashData.reduce((s, c) => s + c.current_cash_minor, 0),
        cashData[0]?.currency || sales.currency,
      ),
      sub: cashData.length > 0
        ? t("dashboard.cashOpenCount", { count: cashData.length })
        : t("dashboard.cashOverviewEmpty"),
      icon: IconCash,
      gradient: "linear-gradient(135deg, #2ecc71, #00b894)",
    },
  ];
  const stats = allStats.filter((s) => {
    if (s.label === t("dashboard.cashOverview")) return canCashState;
    return canAccounts; // salesToday + openOrders
  });

  const nothingToShow = !canAccounts && !canCashState && !canDevices && !canAttendance;
  if (nothingToShow) {
    return (
      <div className="card" style={{ padding: 48, textAlign: "center", color: "var(--muted)", marginTop: 24 }}>
        Na nástěnce není v tuto chvíli nic k zobrazení.
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
        <div>
          <h1>{t("nav.dashboard")}</h1>
          <p>{t("dashboard.subtitle", { date: new Date().toLocaleDateString("cs-CZ", { weekday: "long", day: "numeric", month: "long" }) })}</p>
        </div>
        {branches.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <IconBranch size={18} className="muted" />
          <SearchableSelect
            value={selectedBranch}
            onChange={setSelectedBranch}
            options={[{ value: "all", label: t("dashboard.allBranches") }, ...branches.map(b => ({ value: b.id, label: b.name }))]}
            placeholder={t("dashboard.allBranches")}
            searchPlaceholder={t("dashboard.searchBranch")}
          />
        </div>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-3" style={{ marginBottom: 28 }}>
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              className="card"
              style={{ padding: 20, position: "relative", overflow: "hidden", cursor: s.to ? "pointer" : undefined }}
              onClick={s.to ? () => nav(s.to!) : undefined}
            >
              <div style={{
                position: "absolute", top: 0, right: 0, width: 80, height: 80,
                borderRadius: "50%", filter: "blur(30px)", opacity: 0.15,
                background: s.gradient,
              }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: "var(--radius-sm)",
                  background: s.gradient, display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#1a1200",
                }}>
                  <Icon size={22} />
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.5 }}>{s.value}</div>
              <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>{s.label}</div>
              <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 8 }}>{s.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Recent orders */}
      {canAccounts && (
      <div className="card" style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>{t("dashboard.recentOrders")}</h3>
            <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>{t("dashboard.recentOrdersSubtitle")}</p>
          </div>
          <IconSparkle size={20} className="muted" />
        </div>
        {orders.length === 0 ? (
          <div style={{ color: "var(--muted)", fontSize: 14, padding: "20px 0", textAlign: "center" }}>
            {t("dashboard.noOrders")}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t("dashboard.orderNumber")}</th>
                <th>{t("dashboard.orderBranch")}</th>
                <th>{t("dashboard.orderAmount")}</th>
                <th>{t("dashboard.orderTime")}</th>
                <th>{t("dashboard.orderStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td style={{ fontWeight: 600 }} className="mono">{o.number || "—"}</td>
                  <td>{o.branch_name}</td>
                  <td style={{ fontWeight: 600 }}>{formatMoney(o.total_minor, o.currency)}</td>
                  <td style={{ color: "var(--muted)" }}>{formatTimeAgo(o.opened_at, t)}</td>
                  <td>
                    <span className={STATUS_BADGES[o.status] || "badge"}>
                      {statusLabels[o.status] || o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

      <div className="grid grid-3" style={{ marginBottom: 28 }}>
        {/* Devices */}
        {canDevices && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{t("dashboard.devicesAndLicenses")}</h3>
              <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>
                {t("dashboard.devicesSummary", { active: devs.active, offline: devs.offline, expiring: devs.expiring_soon, expired: devs.expired })}
              </p>
            </div>
            <IconDevice size={20} className="muted" />
          </div>
          {devs.devices.length === 0 ? (
            <div style={{ color: "var(--muted)", fontSize: 14, padding: "20px 0", textAlign: "center" }}>
              {t("dashboard.noDevices")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {devs.devices.map((d) => (
                <div key={d.id} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px", borderRadius: "var(--radius-sm)",
                  background: "var(--bg-elevated)", border: "1px solid var(--border)",
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "var(--radius-sm)",
                    background: d.is_active ? "var(--gradient-sunset)" : "var(--bg)",
                    border: d.is_active ? "none" : "1px solid var(--border)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 700, color: d.is_active ? "#1a1200" : "var(--muted)",
                    flexShrink: 0,
                  }}>
                    <IconDevice size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{d.name}</div>
                    <div style={{ color: "var(--muted)", fontSize: 12 }}>
                      {d.license_type ? `${t(`devices.type.${d.license_type}`, d.license_type)} · ` : t("dashboard.noLicense")}
                      {d.days_remaining !== null
                        ? d.days_remaining > 0
                          ? t("dashboard.daysRemaining", { count: d.days_remaining })
                          : t("dashboard.expiresToday")
                        : "—"}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span className={
                      d.days_remaining === null
                        ? "badge badge-red"
                        : d.days_remaining <= 7
                          ? "badge badge-yellow"
                          : "badge badge-green"
                    }>
                      {d.is_active ? t("dashboard.online") : t("dashboard.offline")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Open shifts */}
        {canAttendance && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{t("dashboard.openShifts")}</h3>
              <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>
                {shifts.length > 0 ? t("dashboard.openShiftsCount", { count: shifts.length }) : t("dashboard.openShiftsEmpty")}
              </p>
            </div>
            <IconClock size={20} className="muted" />
          </div>
          {shifts.length === 0 ? (
            <div style={{ color: "var(--muted)", fontSize: 14, padding: "20px 0", textAlign: "center" }}>
              {t("dashboard.noShifts")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {shifts.map((s) => (
                <div key={s.id} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px", borderRadius: "var(--radius-sm)",
                  background: "var(--bg-elevated)", border: "1px solid var(--border)",
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: "var(--gradient-sunset)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 700, color: "#1a1200", flexShrink: 0,
                  }}>{s.employee_name.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{s.employee_name}</div>
                    <div style={{ color: "var(--muted)", fontSize: 12 }}>
                      {s.branch_name} · {t("dashboard.shiftSince")} {new Date(s.started_at).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="badge badge-green">{formatDuration(s.duration_minutes)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Cash overview */}
        {canCashState && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{t("dashboard.cashOverview")}</h3>
              <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>{t("dashboard.cashOverviewSubtitle")}</p>
            </div>
            <IconCash size={20} className="muted" />
          </div>
          {cashData.length === 0 ? (
            <div style={{ color: "var(--muted)", fontSize: 14, padding: "20px 0", textAlign: "center" }}>
              {t("dashboard.cashOverviewEmpty")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {cashData.map((c) => (
                <div key={c.shift_id} style={{
                  display: "flex", flexDirection: "column", gap: 10,
                  padding: "12px 14px", borderRadius: "var(--radius-sm)",
                  background: "var(--bg-elevated)", border: "1px solid var(--border)",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{c.branch_name}</div>
                    <div style={{ color: "var(--muted)", fontSize: 12 }}>
                      {c.employee_name} · {new Date(c.opened_at).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--muted)" }}>{t("dashboard.openingCash")}</span>
                      <strong>{formatMoney(c.opening_cash_minor, c.currency)}</strong>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--muted)" }}>{t("dashboard.cashSales")}</span>
                      <strong>{formatMoney(c.cash_sales_minor, c.currency)}</strong>
                    </div>
                    {c.cash_ops_minor !== 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--muted)" }}>{t("dashboard.cashOps")}</span>
                        <strong>{formatMoney(c.cash_ops_minor, c.currency)}</strong>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--border)", paddingTop: 6, marginTop: 4 }}>
                      <span style={{ color: "var(--muted)" }}>{t("dashboard.currentCash")}</span>
                      <strong>{formatMoney(c.current_cash_minor, c.currency)}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

    </div>
  );
}
